import { useState } from 'react'
import { ChevronDown, Check, Loader2 } from '@/ui/icons'
import { Button } from '@/ui/design-system'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/ui/components/primitives/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/components'
import { cn } from '@/shared/utils'
import { MilitaryRole, MilitaryRoleInfo, MILITARY_ROLES } from '@/shared/api/militaryRoles'

interface MilitaryRoleSelectorProps {
  currentRole?: MilitaryRole
  availableRoles: MilitaryRoleInfo[]
  onRoleChange: (role: MilitaryRole) => void
  disabled?: boolean
  isLoading?: boolean
  className?: string
  /** Optional member name shown in dialog title (e.g. "Assign role for Jane") */
  memberName?: string
  /** 'modal' = full dialog (default); 'dropdown' = dropdown menu like Add Stop */
  variant?: 'modal' | 'dropdown'
}

export function MilitaryRoleSelector({
  currentRole,
  availableRoles,
  onRoleChange,
  disabled = false,
  isLoading = false,
  className = '',
  memberName,
  variant = 'modal',
}: MilitaryRoleSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)

  const currentRoleInfo = currentRole
    ? MILITARY_ROLES.find((role) => role.value === currentRole)
    : null

  const handleRoleSelect = (role: MilitaryRole) => {
    onRoleChange(role)
    setIsOpen(false)
  }

  const handleClearRole = () => {
    onRoleChange(null)
    setIsOpen(false)
  }

  const triggerButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={variant === 'modal' ? () => !disabled && !isLoading && setIsOpen(true) : undefined}
      disabled={disabled || isLoading}
      className={cn(
        'min-w-[200px] justify-between text-left h-9',
        'border-earth-200 text-earth-700 dark:border-earth-600 dark:text-earth-200',
        'hover:bg-earth-100 hover:text-earth-800 hover:border-earth-300 dark:hover:bg-earth-800/50 dark:hover:text-earth-100',
        currentRoleInfo
          ? 'border-primary/20 bg-primary/5 hover:bg-primary/10 hover:text-primary-foreground hover:border-primary/30'
          : ''
      )}
    >
      <div className="flex items-center space-x-2 flex-1 min-w-0">
        {currentRoleInfo ? (
          <span className="text-sm font-medium truncate">{currentRoleInfo.label}</span>
        ) : (
          <span className="text-sm text-earth-600 dark:text-earth-400 truncate">Assign Role</span>
        )}
      </div>
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
      ) : (
        <ChevronDown className="w-4 h-4 flex-shrink-0 text-earth-500" />
      )}
    </Button>
  )

  const dropdownContent = (
    <>
      <DropdownMenuItem onClick={handleClearRole} className="flex items-center gap-2">
        <div className="flex flex-col flex-1 min-w-0">
          <span className="font-medium">No Role</span>
          <span className="text-xs text-muted-foreground">Remove assigned role</span>
        </div>
        {!currentRole && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      {availableRoles.map((role) => (
        <DropdownMenuItem
          key={role.value || 'none'}
          onClick={() => handleRoleSelect(role.value)}
          className="flex items-center gap-2"
        >
          <div className="flex flex-col flex-1 min-w-0">
            <span className="font-medium">{role.label}</span>
            <span className="text-xs text-muted-foreground">{role.description}</span>
          </div>
          {currentRole === role.value && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
        </DropdownMenuItem>
      ))}
      {availableRoles.length === 0 && (
        <div className="px-2 py-4 text-center text-muted-foreground text-sm">
          All roles have been assigned for this trip.
        </div>
      )}
    </>
  )

  if (variant === 'dropdown') {
    return (
      <div className={className}>
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild disabled={disabled || isLoading}>
            {triggerButton}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56 max-h-[min(70vh,400px)] overflow-y-auto">
            {dropdownContent}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )
  }

  return (
    <div className={className}>
      {triggerButton}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 pr-12 border-b border-gray-100">
            <DialogTitle>
              {memberName ? `Assign role for ${memberName}` : 'Assign trip role'}
            </DialogTitle>
            <DialogDescription>
              Choose a role for this crew member. Each role can only be assigned to one person per trip.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 min-h-0">
            {/* No Role option */}
            <button
              onClick={handleClearRole}
              className="w-full px-4 py-3 text-left rounded-lg hover:bg-gray-50 focus:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/20 flex items-center gap-3 transition-colors mb-2"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900">No Role</div>
                <div className="text-sm text-gray-500 mt-0.5">Remove assigned role</div>
              </div>
              {!currentRole && (
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-primary" />
                </div>
              )}
            </button>

            <div className="border-t border-gray-100 my-3" />

            {/* Role options with labels and descriptions */}
            <div className="space-y-1">
              {availableRoles.map((role) => (
                <button
                  key={role.value || 'none'}
                  onClick={() => handleRoleSelect(role.value)}
                  className="w-full px-4 py-3 text-left rounded-lg hover:bg-gray-50 focus:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/20 flex items-center gap-3 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">{role.label}</div>
                    <div className="text-sm text-gray-500 mt-0.5">{role.description}</div>
                  </div>
                  {currentRole === role.value && (
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-primary" />
                    </div>
                  )}
                </button>
              ))}
            </div>

            {availableRoles.length === 0 && (
              <div className="py-8 text-center text-gray-500">
                <p className="font-medium text-gray-700">No available roles</p>
                <p className="text-sm mt-1">All roles have been assigned for this trip.</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Role Badge Component for displaying assigned roles
interface MilitaryRoleBadgeProps {
  role: MilitaryRole
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

export function MilitaryRoleBadge({ role, className = '', size = 'md' }: MilitaryRoleBadgeProps) {
  const roleInfo = role ? MILITARY_ROLES.find((r) => r.value === role) : null

  if (!roleInfo) return null

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base',
  }

  return (
    <div
      className={`inline-flex items-center space-x-2 rounded-full font-medium border ${roleInfo.color} ${sizeClasses[size]} ${className}`}
    >
      <span className="truncate">{roleInfo.label}</span>
    </div>
  )
}
